import { Link } from "react-router-dom";
import { Wrench, Cpu, TreeStructure, ListChecks, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxhdXRvbW90aXZlJTIwdGVjaG5pY2lhbiUyMHdvcmtpbmd8ZW58MHx8fHwxNzg1MDA5ODMyfDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground bg-grain">
      {/* Nav */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
              <Wrench weight="bold" className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-xl tracking-tight leading-none">DYNAMIC DIAG</div>
              <div className="text-[10px] font-mono-tech text-muted-foreground tracking-widest">AI DIAGNOSTIC ENGINE</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/auth?mode=login">
              <Button variant="ghost" data-testid="nav-login-btn">Log in</Button>
            </Link>
            <Link to="/auth?mode=signup">
              <Button data-testid="nav-signup-btn" className="bg-primary hover:bg-primary/90">
                Get started <ArrowRight weight="bold" className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/80 to-background" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-border rounded-full text-xs font-mono-tech text-muted-foreground mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              POWERED BY DYNAMIC DIAG · REASONING ENGINE
            </div>
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Diagnose faults<br />
              <span className="text-primary">without the guesswork.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Enter the year, make, model, complaint and any DTCs. Dynamic Diag walks you through a targeted
              test procedure &mdash; step by step &mdash; until you have a root cause and a repair plan.
              Built for working technicians, not armchair mechanics.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auth?mode=signup">
                <Button size="lg" className="bg-primary hover:bg-primary/90 min-h-12 px-6" data-testid="hero-cta-btn">
                  Start a diagnostic
                  <ArrowRight weight="bold" className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/auth?mode=login">
                <Button size="lg" variant="outline" className="min-h-12 px-6" data-testid="hero-login-btn">
                  I already have an account
                </Button>
              </Link>
            </div>
          </div>

          <div className="md:col-span-4 fade-up hidden md:block" style={{ animationDelay: "120ms" }}>
            <div className="border border-border rounded-lg p-5 bg-card">
              <div className="text-xs font-mono-tech text-muted-foreground mb-3">// LIVE SESSION</div>
              <div className="space-y-3 text-sm">
                <Row k="VEHICLE" v="2018 Ford F-150 3.5L" mono />
                <Row k="COMPLAINT" v="Misfire, rough idle warm" />
                <Row k="DTC" v="P0302 · P0316" mono />
                <div className="border-t border-border pt-3 mt-3">
                  <div className="text-xs text-muted-foreground mb-2">Top candidates</div>
                  <Cause label="Cyl 2 ignition coil" level="high" />
                  <Cause label="Fuel injector fault" level="medium" />
                  <Cause label="Timing chain wear" level="low" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-6">
          <Feature
            icon={<Cpu weight="bold" className="w-6 h-6" />}
            title="AI Root-Cause Engine"
            desc="Our reasoning engine works through the case like a shop foreman and ranks the likely causes before you touch a wrench."
          />
          <Feature
            icon={<TreeStructure weight="bold" className="w-6 h-6" />}
            title="Adaptive Flow Chart"
            desc="Every answer you give reshapes the diagnostic path so you always test the highest-information-gain node next."
          />
          <Feature
            icon={<ListChecks weight="bold" className="w-6 h-6" />}
            title="Real Test Procedures"
            desc="Tools required, expected values, and safety warnings for every step. No fluff. Just what to check and how."
          />
        </div>
      </section>

      {/* Sample report walkthrough */}
      <section className="border-t border-border" id="sample-report">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
            <div>
              <div className="text-xs font-mono-tech text-primary uppercase tracking-widest mb-2">// what your customer walks away with</div>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">EVERY CASE ENDS<br/>WITH A PRINTED RECEIPT.</h2>
              <p className="text-muted-foreground mt-3 max-w-xl">
                Once the AI locks in a root cause, one tap prints a branded, plain-English report for the customer.
                Hover the numbered pins to see what each block does.
              </p>
            </div>
          </div>

          <div className="mt-8 grid lg:grid-cols-12 gap-6">
            {/* Mock report — light theme */}
            <div className="lg:col-span-8 relative border border-border rounded-lg bg-white text-gray-900 p-6 md:p-8 shadow-2xl">
              <SampleReport />
            </div>

            {/* Legend */}
            <div className="lg:col-span-4 space-y-3">
              {[
                ["01", "Shop letterhead", "Your logo, name, phone, and address stamp every printout. Nothing generic."],
                ["02", "Vehicle & complaint", "The exact car, engine, VIN, customer complaint and DTCs — locked to this repair order."],
                ["03", "Confirmed root cause", "The AI's final answer after walking through targeted tests with the tech."],
                ["04", "Plain-English explanation", "The same fix rewritten so a non-mechanic customer actually understands what broke."],
                ["05", "Parts & prices", "OEM #s and aftermarket alternatives. Tap any cell to edit, click the icon to shop live."],
                ["06", "Out-the-door total", "Parts + your markup + labor (hrs × rate) + tax — with toggles to hide labor or parts from the customer."],
              ].map(([n, t, d]) => (
                <div key={n} className="border border-border rounded-md p-4 bg-card hover:border-primary/50 transition-colors">
                  <div className="flex items-baseline gap-3">
                    <div className="font-mono-tech text-primary text-xs w-8 shrink-0">{n}</div>
                    <div>
                      <div className="font-display font-semibold text-lg leading-tight">{t}</div>
                      <div className="text-sm text-muted-foreground mt-0.5">{d}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-border bg-card/40">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">HOW IT WORKS</h2>
          <div className="mt-10 grid md:grid-cols-4 gap-5">
            {[
              ["01", "Enter the vehicle", "Manual Y/M/M or decode a VIN."],
              ["02", "Describe the complaint", "Customer symptoms + any DTCs or observations."],
              ["03", "Follow the flow", "Test, answer, advance. The tree adapts as you go."],
              ["04", "Confirm the fix", "Root cause, repair plan, estimated labor."],
            ].map(([n, t, d]) => (
              <div key={n} className="border border-border rounded-md p-5 bg-background">
                <div className="font-mono-tech text-primary text-sm">{n}</div>
                <div className="font-display font-semibold text-2xl mt-1">{t}</div>
                <div className="text-sm text-muted-foreground mt-2">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
          <div>© {new Date().getFullYear()} Dynamic Diag · Built for the shop floor.</div>
          <div className="flex items-center gap-5 flex-wrap">
            <span>For Questions/Concerns email <a href="mailto:Admin@JLDynamic.com" className="text-primary hover:underline" data-testid="footer-contact-email">Admin@JLDynamic.com</a></span>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <span className="font-mono-tech text-xs">v0.1 · MVP</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SampleReport() {
  return (
    <div className="relative text-sm leading-relaxed">
      {/* 01: Letterhead */}
      <Pin n="01" className="-left-11 top-2" />
      <div className="flex items-start justify-between gap-4 border-b-2 border-gray-800 pb-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-md bg-primary flex items-center justify-center">
            <Wrench weight="bold" className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-2xl leading-none tracking-tight">TORRES AUTO REPAIR</div>
            <div className="text-xs text-gray-600 mt-1">123 Grease Way, Denver, CO 80202</div>
            <div className="text-[11px] text-gray-500 mt-0.5 font-mono-tech">(555) 987-6543 · hello@torresauto.com</div>
          </div>
        </div>
        <div className="text-right text-[10px] font-mono-tech text-gray-600 shrink-0">
          <div>REPORT #1A89FA42C495</div>
          <div className="mt-1">CREATED 07/25/2026</div>
        </div>
      </div>

      {/* 02: Vehicle & complaint */}
      <div className="relative grid grid-cols-2 gap-6 mt-4">
        <Pin n="02" className="-left-11 top-0" />
        <div>
          <Small>Vehicle</Small>
          <div className="font-display text-xl font-semibold leading-tight">2018 Ford F-150</div>
          <div className="text-[11px] font-mono-tech text-gray-600">3.5L V6 Turbo (EcoBoost) · VIN 1FTFW1E5XJKD…</div>
        </div>
        <div>
          <Small>Confidence</Small>
          <div className="font-display text-xl font-semibold uppercase">HIGH</div>
        </div>
      </div>

      <div className="mt-4">
        <Small>Customer Complaint</Small>
        <div>Engine misfires when warm, rough idle, check engine light on.</div>
      </div>
      <div className="mt-2">
        <Small>Observations / DTCs</Small>
        <div className="font-mono-tech text-xs">DTC: P0302, P0316. Fuel trim +18% at idle. Plugs look OK.</div>
      </div>

      {/* 03: Root cause */}
      <div className="relative mt-5 border-2 border-primary rounded-md p-4">
        <Pin n="03" className="-left-12 top-1" />
        <Small className="text-primary">Confirmed Root Cause</Small>
        <div className="font-display font-bold text-2xl leading-tight mt-0.5">Faulty Cylinder 2 Ignition Coil</div>
      </div>

      {/* 04: Plain English */}
      <div className="relative mt-4 border border-gray-300 rounded-md p-4 bg-gray-50">
        <Pin n="04" className="-left-12 top-1" />
        <Small>What This Means (Plain English)</Small>
        <div className="text-gray-800">
          The part that fires the spark plug on one of your engine's cylinders failed. That's why the engine
          shook at idle and the check engine light came on. Replacing that single coil restores smooth running.
        </div>
      </div>

      {/* 05: Parts */}
      <div className="relative mt-5">
        <Pin n="05" className="-left-12 top-0" />
        <Small>Parts Required</Small>
        <div className="border border-gray-300 rounded-md overflow-hidden mt-1">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 text-gray-600">
              <tr className="text-left">
                <th className="px-2 py-1.5 font-mono-tech text-[9px] uppercase tracking-widest">Part</th>
                <th className="px-2 py-1.5 font-mono-tech text-[9px] uppercase tracking-widest">OEM #</th>
                <th className="px-2 py-1.5 font-mono-tech text-[9px] uppercase tracking-widest">Aftermarket</th>
                <th className="px-2 py-1.5 font-mono-tech text-[9px] uppercase tracking-widest text-right w-10">Qty</th>
                <th className="px-2 py-1.5 font-mono-tech text-[9px] uppercase tracking-widest text-right w-14">$</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gray-200">
                <td className="px-2 py-1.5">Ignition Coil · Cyl 2</td>
                <td className="px-2 py-1.5 font-mono-tech">BL3Z-12029-C</td>
                <td className="px-2 py-1.5 text-gray-700">Motorcraft, Delphi</td>
                <td className="px-2 py-1.5 text-right font-mono-tech">1</td>
                <td className="px-2 py-1.5 text-right font-mono-tech">55</td>
              </tr>
              <tr className="border-t border-gray-200">
                <td className="px-2 py-1.5">Iridium Spark Plug</td>
                <td className="px-2 py-1.5 font-mono-tech">SP-534</td>
                <td className="px-2 py-1.5 text-gray-700">NGK, Denso</td>
                <td className="px-2 py-1.5 text-right font-mono-tech">6</td>
                <td className="px-2 py-1.5 text-right font-mono-tech">12</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 06: Totals */}
      <div className="relative mt-4">
        <Pin n="06" className="-left-12 top-0" />
        <Small>Estimate</Small>
        <div className="border border-gray-300 rounded-md overflow-hidden mt-1">
          <table className="w-full text-xs">
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="px-2 py-1.5 text-gray-700">Parts subtotal</td>
                <td className="px-2 py-1.5 text-right font-mono-tech">$127.00</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="px-2 py-1.5 text-gray-700">Parts markup (35%)</td>
                <td className="px-2 py-1.5 text-right font-mono-tech">+ $44.45</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="px-2 py-1.5 text-gray-700">Labor <span className="text-[10px] text-gray-500 font-mono-tech ml-1">2.5 hr × $145/hr</span></td>
                <td className="px-2 py-1.5 text-right font-mono-tech">$362.50</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="px-2 py-1.5 text-gray-700">Sales tax (7.75%)</td>
                <td className="px-2 py-1.5 text-right font-mono-tech">+ $41.38</td>
              </tr>
              <tr className="bg-gray-100">
                <td className="px-2 py-2 font-display text-base font-bold uppercase tracking-tight">Grand Total</td>
                <td className="px-2 py-2 text-right font-display text-lg font-bold">$575.33</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Pin({ n, className = "" }) {
  return (
    <div className={`absolute w-8 h-8 rounded-full bg-primary text-white font-mono-tech text-[11px] font-bold flex items-center justify-center shadow-lg ring-4 ring-white z-10 ${className}`}>
      {n}
    </div>
  );
}

function Small({ children, className = "" }) {
  return (
    <div className={`text-[9px] font-mono-tech text-gray-500 uppercase tracking-widest mb-0.5 ${className}`}>
      {children}
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="border border-border rounded-md p-6 bg-card hover:border-primary/50 transition-colors">
      <div className="w-11 h-11 rounded-md bg-primary/10 border border-primary/30 text-primary flex items-center justify-center">
        {icon}
      </div>
      <div className="font-display font-semibold text-2xl mt-4 tracking-tight">{title}</div>
      <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</div>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-muted-foreground font-mono-tech">{k}</span>
      <span className={mono ? "font-mono-tech" : ""}>{v}</span>
    </div>
  );
}

function Cause({ label, level }) {
  const colors = { high: "bg-destructive/20 text-destructive border-destructive/40", medium: "bg-warning/20 text-warning border-warning/40", low: "bg-muted text-muted-foreground border-border" };
  const color = level === "high" ? "text-red-400 border-red-500/40 bg-red-500/10" : level === "medium" ? "text-yellow-400 border-yellow-500/40 bg-yellow-500/10" : "text-muted-foreground border-border bg-muted";
  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-2 text-sm">
        <CheckCircle weight="bold" className="w-4 h-4 text-muted-foreground" />
        {label}
      </div>
      <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase font-mono-tech ${color}`}>{level}</span>
    </div>
  );
}
